import {
  Banner,
  Modal,
  MultiSelect,
  PriceBandIcon,
  PricingIcon,
  PrimaryButton,
  SearchField,
  SecondaryButton,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import styles from './AddProductsToCategoryModal.module.scss';
import SelectableListWithControls from 'src/components/SelectableListWithControls';
import useProductPrices from 'src/hooks/useProductPrices/useProductPrices';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useUpdatePricingCategoryProducts } from 'src/hooks/useUpdatePricingCategoryProducts/useUpdatePricingCategoryProducts';
import { usePricingCategories } from 'src/hooks/usePricingCategories';
import { SelectableListOption } from 'src/components/SelectableList/SelectableList';
import { IProductPrice } from 'src/types/productPricing.types';
import {
  nestedCategoriesToSelectOptions,
  stripGroupIds,
  UNCATEGORISED_ID,
} from 'src/utils/venueCategories/mappedVenueCategories';

const toProductOption = (product: IProductPrice): SelectableListOption => ({
  id: product.item_id,
  label: product.name,
  subtitle: product.ranges?.map((r) => r.name).join(', ') || undefined,
  rightText: product.pricing_category?.name || undefined,
});

interface AddProductsToCategoryModalProps {
  open: boolean;
  closeModal: () => void;
  categoryName: string;
  categoryId: string;
  priceSchemeId: string;
}

export const AddProductsToCategoryModal = ({
  open,
  closeModal,
  categoryName,
  categoryId,
  priceSchemeId,
}: AddProductsToCategoryModalProps) => {
  const [itemIds, setItemIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRanges, setSelectedRanges] = useState<string[]>([]);
  const [selectedPricingCategories, setSelectedPricingCategories] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [knownProductsMap, setKnownProductsMap] = useState<Map<string, SelectableListOption>>(new Map());
  const hasInitializedRef = useRef(false);

  const debouncedSearchTerm = useDebounce(searchTerm);

  const { data: ranges } = useRanges();
  const rangeOptions = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) || [],
    [ranges],
  );

  const { nestedCategories } = usePricingCategories(priceSchemeId);
  const categoryOptions = useMemo(
    () =>
      nestedCategoriesToSelectOptions(nestedCategories, {
        parentAsLeafLabel: (name) => `Unassigned to sub-category (${name})`,
      }),
    [nestedCategories],
  );

  // Fetch all products already in this category, paginating until complete
  const {
    data: categoryProductsData,
    isLoading: isLoadingCategoryProducts,
    hasNextPage: hasNextCategoryPage,
    fetchNextPage: fetchNextCategoryPage,
    isFetchingNextPage: isFetchingNextCategoryPage,
  } = useProductPrices(priceSchemeId, 50, {
    pricingCategories: [categoryId],
    enabled: open,
    refetchOnMount: 'always',
    cacheTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (hasNextCategoryPage && !isFetchingNextCategoryPage) {
      fetchNextCategoryPage();
    }
  }, [hasNextCategoryPage, isFetchingNextCategoryPage, fetchNextCategoryPage]);

  // Main paginated query with search/filter options
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useProductPrices(priceSchemeId, 50, {
    searchParam: debouncedSearchTerm || undefined,
    ranges: selectedRanges.length > 0 ? selectedRanges : undefined,
    pricingCategories:
      selectedPricingCategories.length > 0
        ? stripGroupIds(selectedPricingCategories).map((id) => (id === UNCATEGORISED_ID ? 'uncategorised' : id))
        : undefined,
    enabled: open,
    refetchOnMount: 'always',
    cacheTime: 0,
    staleTime: 0,
  });

  const productOptions = useMemo(() => data?.pages.flatMap((page) => page.data.map(toProductOption)) ?? [], [data]);

  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      setItemIds([]);
      setSearchTerm('');
      setSelectedRanges([]);
      setSelectedPricingCategories([]);
      setSaveError(false);
      setKnownProductsMap(new Map());
      return;
    }
    if (
      !hasInitializedRef.current &&
      !hasNextCategoryPage &&
      !isFetchingNextCategoryPage &&
      categoryProductsData?.pages
    ) {
      hasInitializedRef.current = true;
      setItemIds(categoryProductsData.pages.flatMap((page) => page.data.map((product) => product.item_id)));
    }
  }, [open, categoryProductsData, hasNextCategoryPage, isFetchingNextCategoryPage]);

  // Accumulate product data into a stable map — entries are never removed so
  // "Currently Selected" stays correct when filters change or pages scroll in
  useEffect(() => {
    setKnownProductsMap((prev) => {
      const newEntries = [
        ...(categoryProductsData?.pages.flatMap((page) => page.data.map(toProductOption)) ?? []),
        ...productOptions,
      ].filter((opt) => !prev.has(opt.id));

      if (newEntries.length === 0) return prev;

      const next = new Map(prev);
      newEntries.forEach((opt) => next.set(opt.id, opt));
      return next;
    });
  }, [categoryProductsData, productOptions]);

  const currentlySelectedOptions = useMemo(
    () => itemIds.map((id) => knownProductsMap.get(id)).filter(Boolean),
    [itemIds, knownProductsMap],
  );

  const { mutateAsync: updateCategoryProducts } = useUpdatePricingCategoryProducts();

  const handleSave = async () => {
    setIsSubmitting(true);
    setSaveError(false);
    try {
      await updateCategoryProducts({ priceSchemeId, categoryId, itemIds });
      closeModal();
    } catch {
      setSaveError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} closeModal={closeModal}>
      <div className={styles['modal-content']}>
        <Typography size="xxlarge">
          Add Product(s) to <span className={styles['bold-text']}>{categoryName}</span>
        </Typography>

        <SelectableListWithControls
          options={productOptions}
          selectedOptionsIds={itemIds}
          onOptionChange={setItemIds}
          collapsible={{
            selectedOptionsData: currentlySelectedOptions,
            isDisabled: isSubmitting,
            isLoading: (isLoadingCategoryProducts || isFetchingNextCategoryPage) && !hasInitializedRef.current,
          }}
          listDetails={{ title: 'Products:', rightText: 'Current Category', isLoading }}
          pagination={{ hasNextPage, onLoadMore: fetchNextPage, isLoadingMore: isFetchingNextPage }}
        >
          <SearchField
            placeholder="Search for a product"
            onChange={(e) => setSearchTerm(e.target.value)}
            isDisabled={isSubmitting}
          />
          {rangeOptions.length > 0 && (
            <MultiSelect
              Icon={PriceBandIcon}
              label="Ranges"
              options={rangeOptions}
              selected={selectedRanges}
              onChange={(ids) => setSelectedRanges(ids)}
              disabled={isSubmitting}
            />
          )}
          {categoryOptions.length > 0 && (
            <MultiSelect
              Icon={PricingIcon}
              label="Pricing categories"
              options={categoryOptions}
              selected={selectedPricingCategories}
              onChange={(ids) => setSelectedPricingCategories(ids)}
              disabled={isSubmitting}
              contentClassName={styles['pricing-categories-content']}
            />
          )}
        </SelectableListWithControls>

        <Banner
          text={
            <>
              <span className={styles['bold-text']}>Products can only exist in a singular category.</span> Proceeding
              with selected products that currently sit in a different category will result in these products being
              moved to this category.
            </>
          }
        />

        <ButtonContainer
          left={
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={closeModal}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton size="full-width" onClick={handleSave} disabled={isSubmitting} loading={isSubmitting}>
              Save
            </PrimaryButton>
          }
        />

        {saveError && <StatusBar variant="error" message="Something went wrong while saving. Please try again." />}
      </div>
    </Modal>
  );
};
